import React, { useState, useEffect } from 'react';
import { Save, Edit, X, User, Settings, Printer, Package, Database, Shield, RefreshCw } from 'lucide-react';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import { userProfileService } from '../../../services/userProfileService';
import { printingService } from '../../../services/printingService';
import { UserProfileDto, UpdateUserProfileRequest } from '../../../types/userProfile';
import { Printer as PrintNodePrinter } from '../../../types/printNode';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/I18nProvider';
import { SupportedLanguage } from '../../../i18n';

interface ProfileSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isEditing?: boolean;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ title, icon, children, isEditing = false }) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {isEditing && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Redigerer</span>}
    </div>
    <div className="space-y-2">
      {children}
    </div>
  </Card>
);

interface ProfileFieldProps {
  label: string;
  value: string | number | boolean | Date | undefined | null;
  type?: 'text' | 'number' | 'boolean' | 'date' | 'select';
  isEditing?: boolean;
  onChange?: (value: string | number | boolean) => void;
  readOnly?: boolean;
  options?: { value: string; label: string }[];
  displayValue?: string; // For showing formatted value when not editing
}

const ProfileField: React.FC<ProfileFieldProps> = ({
  label,
  value,
  type = 'text',
  isEditing = false,
  onChange,
  readOnly = false,
  options = [],
  displayValue
}) => {
  const formatValue = (val: any) => {
    if (displayValue) return displayValue;
    if (val === null || val === undefined) return 'Ikke angitt';
    if (type === 'boolean') return val ? 'Ja' : 'Nei';
    if (type === 'date' && val) return new Date(val).toLocaleDateString('no-NO');
    return val.toString();
  };

  if (isEditing && !readOnly) {
    if (type === 'boolean') {
      return (
        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">{value ? 'Ja' : 'Nei'}</span>
            <button
              onClick={() => onChange?.(!value)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                value ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                value ? 'translate-x-5' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      );
    }

    if (type === 'select') {
      return (
        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
          <span className="text-sm font-medium text-gray-700 w-1/3">{label}</span>
          <select
            value={value?.toString() || ''}
            onChange={(e) => onChange?.(e.target.value)}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
        <span className="text-sm font-medium text-gray-700 w-1/3">{label}</span>
        <input
          type={type}
          value={value?.toString() || ''}
          onChange={(e) => {
            const newValue = type === 'number' ? Number(e.target.value) : e.target.value;
            onChange?.(newValue);
          }}
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <span className={`text-sm text-gray-600 ${
        type === 'boolean' ? (value ? 'text-green-600' : 'text-gray-400') : ''
      }`}>
        {formatValue(value)}
      </span>
    </div>
  );
};

interface PrinterSelectFieldProps {
  label: string;
  value: number | undefined | null;
  printers: PrintNodePrinter[];
  loading?: boolean;
}

const PrinterSelectField: React.FC<PrinterSelectFieldProps> = ({
  label,
  value,
  printers,
  loading = false
}) => {
  const getPrinterName = (printerId: number | undefined | null): string => {
    if (!printerId) return 'Ikke angitt';
    const printer = printers.find(p => p.id === printerId);
    return printer?.name || `Skriver ${printerId}`;
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <span className="text-sm text-gray-600">
        {loading ? <RefreshCw size={14} className="animate-spin text-blue-500" /> : getPrinterName(value)}
      </span>
    </div>
  );
};

export const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const { language, setLanguage } = useTranslation();
  const [profile, setProfile] = useState<UserProfileDto | null>(null);
  const [editingProfile, setEditingProfile] = useState<UserProfileDto | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PrintNode printers state
  const [printers, setPrinters] = useState<PrintNodePrinter[]>([]);
  const [printersLoading, setPrintersLoading] = useState(false);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userProfileService.getCurrentUserProfile();
      setProfile(data);
      setEditingProfile(data);
    } catch (err) {
      setError('Kunne ikke laste profil. Vennligst prøv igjen.');
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPrinters = async () => {
    if (!user?.companyNo) return;

    try {
      setPrintersLoading(true);
      const availablePrinters = await printingService.getAvailablePrinters(user.companyNo);
      setPrinters(availablePrinters);
    } catch (err) {
      console.error('Failed to load printers:', err);
    } finally {
      setPrintersLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadPrinters();
  }, [user?.companyNo]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditingProfile(profile);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingProfile(profile);
  };

  const handleSave = async () => {
    if (!editingProfile) return;

    try {
      setSaving(true);
      setError(null);

      // Create update request from editing profile
      const updateRequest: UpdateUserProfileRequest = {
        companyNo: editingProfile.companyNo,
        profileID: editingProfile.profileID,
        profileName: editingProfile.profileName,
        httpHostname: editingProfile.httpHostname,
        httpPort: editingProfile.httpPort,
        // Pwd utelates med vilje. GET-responsen leverer kryptert pwd; å sende den
        // tilbake ville fått backend til å kryptere på nytt og korrumpere
        // passordet. MapToEntity bevarer eksisterende Pwd når feltet er tomt.
        langID: editingProfile.langID,
        protocol: editingProfile.protocol,
        tcpHostname: editingProfile.tcpHostname,
        tcpPort: editingProfile.tcpPort,
        operator: editingProfile.operator,
        employeeNoERP: editingProfile.employeeNoERP,
        stockCountingActive: editingProfile.stockCountingActive,
        goodsReceptionActive: editingProfile.goodsReceptionActive,
        pickListActive: editingProfile.pickListActive,
        pickListVersion: editingProfile.pickListVersion,
        pickListDetailView: editingProfile.pickListDetailView,
        operatorPwdRequired: editingProfile.operatorPwdRequired,
        operatorRequired: editingProfile.operatorRequired,
        connectOnStartup: editingProfile.connectOnStartup,
        pListApplyChangesEvent: editingProfile.pListApplyChangesEvent,
        pListLastSaveServer: editingProfile.pListLastSaveServer,
        pListLastSaveClient: editingProfile.pListLastSaveClient,
        orderLineSortOrder: editingProfile.orderLineSortOrder,
        userdefinedExtraInfoField: editingProfile.userdefinedExtraInfoField,
        userdefinedExtraInfoLabel: editingProfile.userdefinedExtraInfoLabel,
        showItemPicureYesNo: editingProfile.showItemPicureYesNo,
        lastPictureSync: editingProfile.lastPictureSync,
        closePListOnDeviceYesNo: editingProfile.closePListOnDeviceYesNo,
        barCodeConfirmItem: editingProfile.barCodeConfirmItem,
        userdefinedExtraInfoField2: editingProfile.userdefinedExtraInfoField2,
        labelExportFolder: editingProfile.labelExportFolder,
        packageSlipPrinterName: editingProfile.packageSlipPrinterName,
        printShippingLabelsYesNo: editingProfile.printShippingLabelsYesNo,
        initShippingLabelsYesNo: editingProfile.initShippingLabelsYesNo,
        packageSlipERPYesNo: editingProfile.packageSlipERPYesNo,
        shippingLabelsERPYesNo: editingProfile.shippingLabelsERPYesNo,
        consignmentNoteERPYesNo: editingProfile.consignmentNoteERPYesNo,
        shippingLabelsPrinterName: editingProfile.shippingLabelsPrinterName,
        consignmentNotePrinterName: editingProfile.consignmentNotePrinterName,
        grLastSaveServer: editingProfile.grLastSaveServer,
        grLastSaveClient: editingProfile.grLastSaveClient,
        closeGROnDeviceYesNo: editingProfile.closeGROnDeviceYesNo,
        defaultWarehouse: editingProfile.defaultWarehouse,
        printerID: editingProfile.printerID,
        qtyReservedAsQtyPicked: editingProfile.qtyReservedAsQtyPicked,
        scLastSaveServer: editingProfile.scLastSaveServer,
        scLastSaveClient: editingProfile.scLastSaveClient,
        closeSCOnDeviceYesNo: editingProfile.closeSCOnDeviceYesNo,
        operatorTxtVisible: editingProfile.operatorTxtVisible,
        operatorTxtLabel: editingProfile.operatorTxtLabel,
        stLastSaveServer: editingProfile.stLastSaveServer,
        stLastSaveClient: editingProfile.stLastSaveClient,
        scrLastSaveServer: editingProfile.scrLastSaveServer,
        scrLastSaveClient: editingProfile.scrLastSaveClient,
        barCodeConfirmPackage: editingProfile.barCodeConfirmPackage,
        packageLabelExportFolder: editingProfile.packageLabelExportFolder,
        packagePrinterID: editingProfile.packagePrinterID,
        packageLabelAutoPrint: editingProfile.packageLabelAutoPrint,
        packageCheckOutYesNo: editingProfile.packageCheckOutYesNo,
        packingSlipCheckOutYesNo: editingProfile.packingSlipCheckOutYesNo,
        grUseSTAND005: editingProfile.grUseSTAND005,
        plUseSTAND005: editingProfile.plUseSTAND005,
        plstand005Type: editingProfile.plstand005Type,
        pListConfirmationYesNo: editingProfile.pListConfirmationYesNo,
        useLocationSensor: editingProfile.useLocationSensor,
        stcTransCorrectionDefault: editingProfile.stcTransCorrectionDefault,
        closeSTOnDeviceYesNo: editingProfile.closeSTOnDeviceYesNo,
        closeSCROnDeviceYesNo: editingProfile.closeSCROnDeviceYesNo,
        userdefinedExtraInfoField3: editingProfile.userdefinedExtraInfoField3,
        distributionActive: editingProfile.distributionActive,
        stand005PrinterName: editingProfile.stand005PrinterName,
        profileGroupID: editingProfile.profileGroupID,
        labelPrinterName: editingProfile.labelPrinterName,
        grRejectMissingPO: editingProfile.grRejectMissingPO,
        grPriceAndCurrencyVisible: editingProfile.grPriceAndCurrencyVisible,
        grDefCustomIDNewOrder: editingProfile.grDefCustomIDNewOrder,
        grDefCustomIDFinishOrder: editingProfile.grDefCustomIDFinishOrder,
        scDefCustomIDNewOrder: editingProfile.scDefCustomIDNewOrder,
        scDefCustomIDFinishOrder: editingProfile.scDefCustomIDFinishOrder,
        grAutoLockPO: editingProfile.grAutoLockPO,
        barCodeConfirmLocation: editingProfile.barCodeConfirmLocation,
        barCodeMaxLength: editingProfile.barCodeMaxLength,
        grDefaultPriceFromItem: editingProfile.grDefaultPriceFromItem,
        ordersTxtToERPYesNo: editingProfile.ordersTxtToERPYesNo,
        ordersTxtExportItemNo: editingProfile.ordersTxtExportItemNo,
        defaultPListFilterGroup: editingProfile.defaultPListFilterGroup,
        pListFilterGroupYesNo: editingProfile.pListFilterGroupYesNo,
        pListOverridePriority: editingProfile.pListOverridePriority,
        packageSlipNumOfCopies: editingProfile.packageSlipNumOfCopies,
        packingActive: editingProfile.packingActive,
        pListFinishToPacking: editingProfile.pListFinishToPacking,
        defaultPackZoneFilter: editingProfile.defaultPackZoneFilter,
        pListOrderPackageReportID: editingProfile.pListOrderPackageReportID,
        hidePListSignatureToolButtons: editingProfile.hidePListSignatureToolButtons,
        cloudStorageYesNo: editingProfile.cloudStorageYesNo,
        cloudStorageItemLabelFolder: editingProfile.cloudStorageItemLabelFolder,
        plstand005Reports: editingProfile.plstand005Reports,
        qtyOriginOnInventoryListYesNo: editingProfile.qtyOriginOnInventoryListYesNo,
        cloudStorageStcSortingBoxFolder: editingProfile.cloudStorageStcSortingBoxFolder,
        stcSortingBoxPrinterName: editingProfile.stcSortingBoxPrinterName,
        grCrossDockingYesNo: editingProfile.grCrossDockingYesNo,
        grCrossDockingNumOfDays: editingProfile.grCrossDockingNumOfDays,
        intRelocationActive: editingProfile.intRelocationActive,
        grToIntRelocation: editingProfile.grToIntRelocation,
        allowAdvItemConfigYesNo: editingProfile.allowAdvItemConfigYesNo,
        goodsReceptionVersion: editingProfile.goodsReceptionVersion,
        createNewCustomerOrderYesNo: editingProfile.createNewCustomerOrderYesNo,
        pOrdersFilter: editingProfile.pOrdersFilter,
        pOrderLineFilter: editingProfile.pOrderLineFilter,
        defaultGRBinCode: editingProfile.defaultGRBinCode,
        defaultGRCDBinCode: editingProfile.defaultGRCDBinCode,
        useGRStcSortingBox: editingProfile.useGRStcSortingBox,
        pListAutoCalcMode: editingProfile.pListAutoCalcMode,
        autoPrintPListOrderPackage: editingProfile.autoPrintPListOrderPackage,
        pListWeightRequired: editingProfile.pListWeightRequired,
        pListVolumeRequired: editingProfile.pListVolumeRequired,
        warehouseFilter: editingProfile.warehouseFilter,
        packageReportID: editingProfile.packageReportID,
        grNewLineToLockedPO: editingProfile.grNewLineToLockedPO,
        addOrderLineNoInput: editingProfile.addOrderLineNoInput,
        plUsePListSSCC: editingProfile.plUsePListSSCC,
        cloudStorageSSCCFolder: editingProfile.cloudStorageSSCCFolder,
        extraQtyOnGRConfirm: editingProfile.extraQtyOnGRConfirm,
        orderLineBatchIDOnPList: editingProfile.orderLineBatchIDOnPList,
        useLocationInItemSearchST: editingProfile.useLocationInItemSearchST,
        pListNumOfPalletsRequired: editingProfile.pListNumOfPalletsRequired,
        pickListNavigationFromListView: editingProfile.pickListNavigationFromListView,
        grCheckPutAwaySplit: editingProfile.grCheckPutAwaySplit,
        grDisableEditGRItemBatchNo: editingProfile.grDisableEditGRItemBatchNo,
        profileLogLevel: editingProfile.profileLogLevel,
        shipmentProposalCustomField: editingProfile.shipmentProposalCustomField,
        stand005PrinterNo: editingProfile.stand005PrinterNo,
        stcSortingBoxPrinterNo: editingProfile.stcSortingBoxPrinterNo,
        labelPrinterNo: editingProfile.labelPrinterNo,
        allowedToChangeLocation: editingProfile.allowedToChangeLocation,
        pListNumOfPackagesRequired: editingProfile.pListNumOfPackagesRequired,
        locationNameRequired: editingProfile.locationNameRequired,
        extraQtyOnGRNotAllowed: editingProfile.extraQtyOnGRNotAllowed,
        plstand005ReportsNumOfCopies: editingProfile.plstand005ReportsNumOfCopies,
      };

      const updated = await userProfileService.updateCurrentUserProfile(updateRequest);
      setProfile(updated);
      setEditingProfile(updated);
      setIsEditing(false);
      
      // If language was changed, update i18n system
      if (editingProfile.langID && editingProfile.langID !== profile?.langID) {
        const newLang = editingProfile.langID.toLowerCase() as SupportedLanguage;
        if (newLang === 'nb' || newLang === 'sv' || newLang === 'en') {
          setLanguage(newLang);
        }
      }
    } catch (err) {
      setError('Kunne ikke lagre profil. Vennligst prøv igjen.');
      console.error('Failed to save profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (fieldName: keyof UserProfileDto, value: any) => {
    if (editingProfile) {
      setEditingProfile({
        ...editingProfile,
        [fieldName]: value
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-600 text-center">{error}</div>
        <Button onClick={loadProfile} variant="primary">
          Prøv igjen
        </Button>
      </div>
    );
  }

  if (!profile || !editingProfile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Ingen profil funnet</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Brukerprofil</h2>
          <p className="text-sm text-gray-600">Vis dine profilinnstillinger</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button onClick={handleCancel} variant="secondary" disabled={saving}>
                <X size={14} />
                Avbryt
              </Button>
              <Button onClick={handleSave} variant="success" disabled={saving}>
                <Save size={14} />
                {saving ? 'Lagrer...' : 'Lagre'}
              </Button>
            </>
          ) : (
            <Button onClick={handleEdit} variant="primary">
              <Edit size={14} />
              Endre språk
            </Button>
          )}
        </div>
      </div>

      {/* General Information */}
      <ProfileSection
        title="Generell informasjon"
        icon={<User size={18} className="text-blue-600" />}
        isEditing={isEditing}
      >
        <ProfileField label="Selskap" value={editingProfile.companyNo} readOnly />
        <ProfileField label="Kunde" value={editingProfile.customerNo} readOnly />
        <ProfileField label="Bruker-ID" value={editingProfile.userID} readOnly />
        <ProfileField label="Profil-ID" value={editingProfile.profileID} readOnly />
        <ProfileField label="Profilnavn" value={editingProfile.profileName} readOnly />
        <ProfileField label="Operatør" value={editingProfile.operator} readOnly />
        <ProfileField
          label="Språk"
          value={editingProfile.langID || 'nb'}
          type="select"
          isEditing={isEditing}
          onChange={(value) => updateField('langID', value)}
          options={[
            { value: 'nb', label: 'Norsk bokmål' },
            { value: 'sv', label: 'Svenska' },
            { value: 'en', label: 'English' }
          ]}
          displayValue={
            editingProfile.langID === 'sv' ? 'Svenska' :
            editingProfile.langID === 'en' ? 'English' : 'Norsk bokmål'
          }
        />
        <ProfileField label="Standard lager" value={editingProfile.defaultWarehouse} readOnly />
      </ProfileSection>

      {/* Module Activation */}
      <ProfileSection
        title="Modulaktivering"
        icon={<Settings size={18} className="text-purple-600" />}
      >
        <ProfileField label="Plukklistemodul" value={editingProfile.pickListActive} type="boolean" readOnly />
        <ProfileField label="Varemottak" value={editingProfile.goodsReceptionActive} type="boolean" readOnly />
        <ProfileField label="Lagertelling" value={editingProfile.stockCountingActive} type="boolean" readOnly />
        <ProfileField label="Pakking" value={editingProfile.packingActive} type="boolean" readOnly />
        <ProfileField label="Distribusjon" value={editingProfile.distributionActive} type="boolean" readOnly />
        <ProfileField label="Intern flytting" value={editingProfile.intRelocationActive} type="boolean" readOnly />
      </ProfileSection>

      {/* Printer Settings */}
      <ProfileSection
        title="Skriverinnstillinger"
        icon={<Printer size={18} className="text-orange-600" />}
      >
        <ProfileField label="Skriver-ID" value={editingProfile.printerID} readOnly />
        <PrinterSelectField label="Etikett-skriver" value={editingProfile.labelPrinterNo} printers={printers} loading={printersLoading} />
        <PrinterSelectField label="SSCC/Pall-skriver" value={editingProfile.stand005PrinterNo} printers={printers} loading={printersLoading} />
        <PrinterSelectField label="Sorteringsboks-skriver" value={editingProfile.stcSortingBoxPrinterNo} printers={printers} loading={printersLoading} />
        <ProfileField label="Pakkseddel-skriver" value={editingProfile.packageSlipPrinterName} readOnly />
        <ProfileField label="Fraktlapp-skriver" value={editingProfile.shippingLabelsPrinterName} readOnly />
      </ProfileSection>

      {/* Barcode Settings */}
      <ProfileSection
        title="Strekkode-innstillinger"
        icon={<Package size={18} className="text-red-600" />}
      >
        <ProfileField label="Bekreft vare" value={editingProfile.barCodeConfirmItem} type="boolean" readOnly />
        <ProfileField label="Bekreft pakke" value={editingProfile.barCodeConfirmPackage} type="boolean" readOnly />
        <ProfileField label="Bekreft lokasjon" value={editingProfile.barCodeConfirmLocation} type="boolean" readOnly />
        <ProfileField label="Maks strekkode-lengde" value={editingProfile.barCodeMaxLength} readOnly />
      </ProfileSection>

      {/* Cloud Storage Settings */}
      <ProfileSection
        title="Skylagring"
        icon={<Database size={18} className="text-indigo-600" />}
      >
        <ProfileField label="Skylagring aktiv" value={editingProfile.cloudStorageYesNo} type="boolean" readOnly />
        <ProfileField label="Vareetikett-mappe" value={editingProfile.cloudStorageItemLabelFolder} readOnly />
        <ProfileField label="SSCC-mappe" value={editingProfile.cloudStorageSSCCFolder} readOnly />
      </ProfileSection>

      {/* System Information */}
      <ProfileSection
        title="Systeminformasjon"
        icon={<Shield size={18} className="text-gray-600" />}
      >
        <ProfileField label="ID" value={editingProfile.id} readOnly />
        <ProfileField label="Profilgruppe-ID" value={editingProfile.profileGroupID} readOnly />
        <ProfileField label="Sist oppdatert" value={editingProfile.lastUpdate} type="date" readOnly />
        <ProfileField label="Oppdatert av" value={editingProfile.updatedBy} readOnly />
        <ProfileField label="Opprettet" value={editingProfile.created} type="date" readOnly />
        <ProfileField label="Opprettet av" value={editingProfile.createdBy} readOnly />
      </ProfileSection>
    </div>
  );
};
